import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Typography
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { ApplicationState, AppActionCreators, AppState } from '../../store';
import { AuthApiService } from '../../services/AuthApiService';
import { LoginPayloadType } from '../../interfaces/Auth';
import { ApplicationCodeEnum } from '../../helpers/Constants';
import { ApplicationContext, AppContext } from '../../context/Contexts';
import { useDeviceFingerprint } from '../../hooks/useDeviceFingerprint';
import Logo from '../_shared/Logo';
import LanguageSelector from './LanguageSelector';
import { captureAuthError } from '../../helpers/Sentry';

type LogInProps = AppState & typeof AppActionCreators;

const LogIn: React.FC<LogInProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const location = useLocation();
  const context = useContext(ApplicationContext);
  const translatorService = (context as AppContext).translatorService;
  const deviceId = useDeviceFingerprint();
  const hasShownToast = useRef(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const t = useCallback(
    (key: string, fallback: string) => translatorService.Tranlate(key, fallback),
    [translatorService]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('sessionExpired') === 'true' && !hasShownToast.current) {
      hasShownToast.current = true;
      enqueueSnackbar(
        t(
          'FLEET_SESSION_EXPIRED_TOAST',
          'Your session expired due to inactivity. Please sign in again.'
        ),
        { variant: 'warning' }
      );
      window.history.replaceState(null, '', '/auth/login');
    }
  }, [location.search, t, enqueueSnackbar]);

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const payload: LoginPayloadType = {
        email,
        password,
        app: ApplicationCodeEnum.CONNECT,
        deviceId,
      };

      const response = await AuthApiService.login(payload);
      const data = response.data;

      if (!data.success) {
        enqueueSnackbar(t(data.message || 'ERROR_GENERIC', 'An error occurred'), {
          variant: 'error'
        });
        setIsLoading(false);
        return;
      }

      if (data.twoFactorToken) {
        setIsLoading(false);
        history.push('/auth/2FA', {
          twoFactorToken: data.twoFactorToken,
          email
        });
        return;
      }

      const { access_token, refresh_token } = data.tokenResponse;
      props.SetToken(access_token);
      props.SetRefreshToken(refresh_token);
      await props.SetUser(enqueueSnackbar);
      await props.LoadCurrencyRates(props.appUser?.organization);

      history.push('/');
    } catch (err: any) {
      captureAuthError('Login', err);
      const errorMessage = err?.response?.data?.message || t('ERROR_GENERIC', 'An error occurred');
      enqueueSnackbar(t(errorMessage, errorMessage), { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      style={{ backgroundColor: '#f4f5fd' }}
    >
      <LanguageSelector />
      <Box
        style={{
          backgroundColor: '#fff',
          borderRadius: 8,
          padding: '32px',
          maxWidth: 500,
          width: '100%',
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)'
        }}
      >
        <Logo />

        <ValidatorForm onSubmit={handleSubmit} instantValidate={true}>
          <Box mb={2} mt={2}>
            <TextValidator
              fullWidth
              label={t('FLEET_EMAIL_TEXT_LABEL', 'Email')}
              variant="outlined"
              type="email"
              name="email"
              value={email}
              onChange={(e: any) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
              validators={['required', 'isEmail']}
              errorMessages={[
                t('VALIDATORS_REQUIRED', 'This field is required'),
                t('VALIDATORS_EMAIL', 'Please enter a valid email')
              ]}
            />
          </Box>

          <Box mb={2}>
            <TextValidator
              fullWidth
              label={t('FLEET_PASSWORD_TEXT_LABEL', 'Password')}
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="current-password"
              validators={['required', 'minStringLength:8']}
              errorMessages={[
                t('VALIDATORS_REQUIRED', 'This field is required'),
                t('FLEET_PASSWORD_MIN_LENGTH_MESSAGE', 'Minimum 8 characters')
              ]}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" mb={2} mt={-1}>
            <Typography
              component={Link}
              to="/auth/forgot-password"
              style={{ textDecoration: 'none', color: 'var(--primary)', fontWeight: 500 }}
            >
              {t('FLEET_FORGOT_PASSWORD_TEXT', 'Forgot password?')}
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isLoading}
            style={{ textTransform: 'none' }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('FLEET_LOGIN_TEXT', 'Sign in')
            )}
          </Button>
        </ValidatorForm>
      </Box>
    </Box>
  );
};

export default connect((state: ApplicationState) => state.app, AppActionCreators)(LogIn as any);
